import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import type { OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { Stack } from "~/common/mui";
import { Param, TooltipParam } from "~/features/orders/common/components/v2/Params";
import type { TFunction } from "i18next";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";

const formatNullableDate = (date?: Date) => (date ? formatDate(date, DateFormatType.DATE) : "-");

export const renderHeaderQuantity = (data: OrderLineItemData, t: TFunction) => {
  const subEntries = data.subEntries ?? [];
  const hasSubEntries = subEntries.length > 0;

  const confirmedQty = hasSubEntries
    ? subEntries.reduce((sum, entry) => sum + entry.confirmedQty, 0)
    : data.confirmedQty;

  const requestedQty = hasSubEntries ? subEntries[0]?.qty : data.qty;

  return (
    <TooltipParam
      label={t("quoteListPage.table.header.confirmedQuantity")}
      value={t("common.of.text.with.values", {
        firstValue: confirmedQty,
        secondValue: requestedQty,
      })}
      tooltipContent={
        <Stack gap={0.5}>
          <Param label={t("quoteListPage.table.header.confirmedQuantity")} value={confirmedQty} />

          <Param label={t("quoteListPage.table.header.requestedQuantity")} value={requestedQty} />
        </Stack>
      }
    />
  );
};

export const renderHeaderDeliverDate = (data: OrderLineItemData, t: TFunction) => {
  const subEntries = data.subEntries ?? [];
  const hasSubEntries = subEntries.length > 0;

  const deliveryDate = hasSubEntries
    ? (() => {
        const dates = subEntries.map((item) => item.deliveryDate).filter(Boolean) as Date[];

        if (!dates.length) {
          return "-";
        }

        const oldestDate = new Date(Math.min(...dates.map((d) => d.getTime())));
        return `${formatDate(oldestDate, DateFormatType.DATE)}${dates.length > 1 ? ", ..." : ""}`;
      })()
    : formatNullableDate(data.deliveryDate);

  const showDeliveryLineText = hasSubEntries
    ? `${t("orderDetails.rdd.see.delivery.line.text")}`
    : formatNullableDate(data.deliveryDate);

  const requestedDate = hasSubEntries
    ? formatNullableDate(subEntries[0]?.requestedDeliveryDate)
    : formatNullableDate(data.requestedDeliveryDate);

  return (
    <TooltipParam
      label={t("quoteListPage.table.header.estimatedDeliveryDate")}
      value={deliveryDate}
      footer={requestedDate}
      tooltipContent={
        <Stack gap={0.5}>
          <Param label={t("quoteListPage.table.header.estimatedDeliveryDate")} value={showDeliveryLineText} />
          <Param label={t("quoteListPage.table.header.reqDeliveryDate")} value={requestedDate} />
        </Stack>
      }
    />
  );
};

export const renderHeaderNetValue = (data: OrderLineItemData, t: TFunction) => {
  const subEntries = data.subEntries ?? [];
  const hasSubEntries = subEntries.length > 0;

  const totalValue = hasSubEntries
    ? subEntries.reduce((sum, entry) => sum + (getTotalPrice(entry.netPrice, entry.confirmedQty)?.value ?? 0), 0)
    : getTotalPrice(data.netPrice, data.qty)?.value ?? 0;

  const price = {
    value: totalValue,
    baseValue: totalValue,
    currencyIso: subEntries[0]?.priceForTotal.currencyIso ?? data.priceForTotal?.currencyIso ?? "",
    isSpecial: undefined,
    isSubscription: false,
    rebate: 0,
    surcharge: 0,
    unit: 1,
  };

  return (
    <PriceElements.Stack>
      <PriceElements.TotalPriceLabel variant="bodySmall" price={price} color="text.label" />
      <PriceElements.Price variant="bodyLarge" price={price} fontWeight="bold" color="text.secondary" />
    </PriceElements.Stack>
  );
};
